import { useEffect, useState } from 'react';
import type { OrderDto, UpdateOrderAddressRequest } from '@ecom/contracts';
import { useOrdersStore } from '@/stores/orders.store';
import { useT } from '@/i18n/use-t';
import { Sheet } from '@/components/page';
import { Alert, Button, Field, Input, Textarea } from '@/components/ui';

/**
 * Correcting where an order goes, before it leaves (ADR-059) — the customer
 * who calls because the building number is wrong.
 *
 * The city is shown, not offered: it priced the delivery, and moving an order
 * to another zone at the old price is not a correction. The API keeps the
 * address it replaces on the timeline.
 */
export function AddressSheet({ order, open, onClose }: { order: OrderDto; open: boolean; onClose: () => void }) {
  const t = useT();
  const { updating, currentError, updateAddress } = useOrdersStore();
  const [form, setForm] = useState<UpdateOrderAddressRequest>(() => fromOrder(order));

  useEffect(() => {
    if (open) setForm(fromOrder(order));
  }, [open, order]);

  const set = (key: keyof UpdateOrderAddressRequest) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const valid = form.fullName.trim().length >= 2 && form.phone.trim().length >= 6 && form.line1.trim().length >= 3;

  async function submit() {
    if (!valid) return;
    if (await updateAddress(order.id, form)) onClose();
  }

  return (
    <Sheet
      open={open}
      title={t('editAddressTitle')}
      subtitle={t('editAddressBody')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={updating}>
            {t('cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={!valid || updating}>
            {updating ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <p className="rounded-control bg-surface-sunken px-3 py-2 text-sm text-ink-muted">
          {t('addressCityFixed', { city: order.shippingAddress.city, region: order.shippingAddress.region })}
        </p>
        <Field label={t('fullName')} htmlFor="address-name">
          <Input id="address-name" value={form.fullName} onChange={set('fullName')} maxLength={120} />
        </Field>
        <Field label={t('addressPhone')} htmlFor="address-phone">
          <Input id="address-phone" dir="ltr" inputMode="tel" value={form.phone} onChange={set('phone')} maxLength={30} />
        </Field>
        <Field label={t('addressLine1')} htmlFor="address-line1">
          <Input id="address-line1" value={form.line1} onChange={set('line1')} maxLength={200} />
        </Field>
        <Field label={t('addressLine2')} htmlFor="address-line2" hint={t('optional')}>
          <Input id="address-line2" value={form.line2 ?? ''} onChange={set('line2')} maxLength={200} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('addressDistrict')} htmlFor="address-district" hint={t('optional')}>
            <Input id="address-district" value={form.district ?? ''} onChange={set('district')} maxLength={120} />
          </Field>
          <Field label={t('addressShortCode')} htmlFor="address-short-code" hint={t('addressShortCodeHint')}>
            <Input
              id="address-short-code"
              dir="ltr"
              value={form.shortCode ?? ''}
              onChange={set('shortCode')}
              maxLength={16}
            />
          </Field>
        </div>
        <Field label={t('addressPostalCode')} htmlFor="address-postal" hint={t('optional')}>
          <Input id="address-postal" dir="ltr" value={form.postalCode ?? ''} onChange={set('postalCode')} maxLength={20} />
        </Field>
        <Field label={t('addressDirections')} htmlFor="address-directions" hint={t('optional')}>
          <Textarea id="address-directions" rows={2} value={form.directions ?? ''} onChange={set('directions')} maxLength={300} />
        </Field>
        {currentError ? <Alert>{currentError}</Alert> : null}
      </form>
    </Sheet>
  );
}

function fromOrder(order: OrderDto): UpdateOrderAddressRequest {
  const address = order.shippingAddress;
  return {
    fullName: address.fullName,
    phone: address.phone,
    line1: address.line1,
    line2: address.line2 ?? '',
    district: address.district ?? '',
    shortCode: address.shortCode ?? '',
    postalCode: address.postalCode ?? '',
    directions: address.directions ?? '',
  };
}
